<template>
  <div class="search-results__semences-filters">
    <div class="search-results__semences-filters-title">
      {{ i18n._('Que recherchez-vous ?') }}
    </div>
    <div class="search-results__semences-filters-buttons">
      <ais-toggle-refinement :attribute="organicCode">
        <template #default="{ value, refine }">
          <adn-switch-button
            :id="organicCode"
            :label="i18n._('Agriculture biologique')"
            :name="organicCode"
            :checked="value.isRefined"
            @input="onToggleOrganic(value, refine)"
          />
        </template>
      </ais-toggle-refinement>
      <ais-refinement-list :attribute="cropOadCode" searchable :sort-by="['count:desc']" :limit="100">
        <template #default="{ items, refine, searchForItems }">
          <adn-button-dropdown
            :label="i18n._('Cultures')"
            :selected="selectedCrop.length > 0"
            :selection-count="selectedCrop.length > 0 ? selectedCrop.length : undefined"
          >
            <c-searchable-select
              :values="selectedCrop"
              :items="transformRefinementItems(items)"
              select-type="radio"
              @update="(value) => onUpdateCrop(value, refine)"
              @search="(text) => handleSearch(text, searchForItems)"
            ></c-searchable-select>
          </adn-button-dropdown>
        </template>
      </ais-refinement-list>
    </div>
    <div v-if="additionalFilters.length > 0" class="search-results__semences-subfilters">
      <p class="search-results__semences-subfilters-description">
        {{ i18n._('Filtres liés à la culture sélectionnée') }}
      </p>
      <div class="search-results__semences-subfilters-wrapper">
        <ul class="search-results__semences-subfilters-items">
          <li v-for="additiontalFilter in additionalFilters" :key="additiontalFilter">
            <template v-if="isBooleanFilter(additiontalFilter)">
              <ais-toggle-refinement :attribute="additiontalFilter" :class-names="singleSelectClasses">
                <template #default="{ value, refine }">
                  <adn-switch-button
                    :id="additiontalFilter"
                    :label="getFilterLabel(additiontalFilter)"
                    :name="additiontalFilter"
                    :checked="value.isRefined"
                    @input="onToggleSubFilter(value, refine, additiontalFilter)"
                  />
                </template>
              </ais-toggle-refinement>
            </template>
            <template v-else>
              <ais-refinement-list :attribute="additiontalFilter" :limit="100">
                <template #default="{ items, refine, searchForItems }">
                  <adn-button-dropdown
                    :label="getFilterLabel(additiontalFilter)"
                    :selection-count="
                      getSelectedFilters(additiontalFilter).length > 0
                        ? getSelectedFilters(additiontalFilter).length
                        : undefined
                    "
                  >
                    <c-searchable-select
                      :values="getSelectedFilters(additiontalFilter)"
                      :items="transformRefinementItems(items)"
                      select-type="checkbox"
                      @update="(value) => onUpdateSubFilter(value, refine, additiontalFilter)"
                      @search="(text) => handleSearch(text, searchForItems)"
                    ></c-searchable-select>
                  </adn-button-dropdown>
                </template>
              </ais-refinement-list>
            </template>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { i18n } from '@b2ag/locale'
import { useCustomFilter } from '../../composables/useCustomFilter'
import AdnSwitchButton from '@b2ag/design-system/src/components/AdnSwitchButton/AdnSwitchButton.vue'
import AdnButtonDropdown from '@b2ag/design-system/src/components/AdnButtonDropdown/AdnButtonDropdown.vue'
import CSearchableSelect from '../../components/CSearchableSelect/CSearchableSelect.vue'
import { FACETS_LABELS, FALSY_FACETS } from '../PSearchFilters/facets.setting'
import { AisRefinementList, AisToggleRefinement } from 'vue-instantsearch/vue3/es'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'

const props = defineProps({
  algoliaParams: {
    type: String,
    default: '',
  },
})

const isInitialized = ref(false)
const store = useStore()
const currentSearchText = ref('')
const cropOadCode = 'crop_oad'
const organicCode = 'organic'
const isTrackingProcessing = ref(false)

const {
  additionalFilters,
  selectedCrop,
  selectedFilters,
  handleCropRefinement,
  handleFilterRefinement,
  fetchCustomFilters,
  initializeFromAlgoliaParams,
} = useCustomFilter()

const transformRefinementItems = (items: any[]) => {
  return items.map((item) => ({
    label: item.label,
    value: item.value,
    isRefined: item.isRefined,
    count: item.count,
  }))
}

const getSelectedFilters = (filter: string) => {
  if (!selectedFilters.value || !selectedFilters.value[filter]) {
    return []
  }
  return selectedFilters.value[filter]
}

const initializeFilters = (params: string) => {
  if (params) {
    initializeFromAlgoliaParams(params)
  }
}

const isBooleanFilter = (filter: string) => {
  return filter in FALSY_FACETS
}

const getFilterLabel = (filter: string) => {
  if (FACETS_LABELS[filter]) {
    return FACETS_LABELS[filter]
  }
  if (FALSY_FACETS[filter]) {
    return FALSY_FACETS[filter]
  }
  return filter
}

const singleSelectClasses = {
  'ais-ToggleRefinement--noRefinement': 'search-results__semences-subfilters-items--noRefinement',
}

const handleSearch = (text: string, searchForItems: (text: string) => void) => {
  currentSearchText.value = text
  searchForItems(text)
}

const trackFilterAnalytics = (filterName: string, level: number) => {
  if (isTrackingProcessing.value) return

  const categoryName = store.getters.selectedFamilyName
  const subCategoryName = store.getters.selectedCategoryName

  const payload = {
    category_name: categoryName,
    sub_category_name: subCategoryName,
    level_click_filter: level,
    click_filter: filterName,
    search_term: currentSearchText.value,
  }

  isTrackingProcessing.value = true

  useAnalyticService().trackEvent('page_search_results_click_OAD_filter', payload)

  setTimeout(() => {
    isTrackingProcessing.value = false
  }, 50)
}

const onUpdateCrop = (value: string[], refine: (value: string) => void) => {
  handleCropRefinement(value, refine)
  trackFilterAnalytics(cropOadCode, 0)
}

const onToggleOrganic = (value: any, refine: (value: any) => void) => {
  refine(value)
  trackFilterAnalytics(organicCode, 0)
}

const onToggleSubFilter = (value: any, refine: (value: any) => void, filterName: string) => {
  refine(value)
  trackFilterAnalytics(filterName, 1)
}

const onUpdateSubFilter = (value: string[], refine: (value: string) => void, filterName: string) => {
  handleFilterRefinement(value, refine, filterName)
  trackFilterAnalytics(filterName, 1)
}

watch(
  () => props.algoliaParams,
  (newParams) => {
    if (isInitialized.value && props.algoliaParams === newParams) return
    setTimeout(() => initializeFilters(newParams), 1)
  },
)

onMounted(async () => {
  await fetchCustomFilters()
  setTimeout(() => initializeFilters(props.algoliaParams), 100)
})
</script>

<style lang="scss">
.search-results__semences-filters {
  background-color: var(--color-main-background);
  border-radius: var(--border-radius-m);
  padding: var(--spacing-3) var(--spacing-3);
  margin-top: var(--spacing-4);
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  &-title {
    font-size: var(--font-size-l);
    font-weight: 700;
    line-height: var(--line-height-l);
  }

  &-buttons {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: var(--spacing-1);
    margin-top: var(--spacing-2);
    width: calc(100% + 2 * var(--spacing-3));
    margin-left: calc(-1 * var(--spacing-3));
    margin-right: calc(-1 * var(--spacing-3));
    padding-left: var(--spacing-3);
    padding-right: var(--spacing-3);
    padding-bottom: var(--spacing-2);

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.adn-action-button,
.adn-switch-button {
  @media screen and (min-width: $breakpoint-lg) {
    font-size: var(--font-size-m) !important;
    height: 4rem !important;
  }
}

.search-results__semences-subfilters {
  &-description {
    font-weight: 700;
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    margin-bottom: var(--spacing-2);
  }

  &-wrapper {
    position: relative;
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 4px;
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: var(--idf-color-main-100);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--idf-color-neutral-600);
      border-radius: 2px;
    }
  }

  &-items {
    display: flex;
    position: relative;
    gap: var(--spacing-1);
    padding-bottom: var(--spacing-2);
    width: max-content;

    &--noRefinement {
      display: none;
    }
  }
}
</style>
